<script  lang="ts" setup>
import { computed, ref } from 'vue';

import { BTTExcelReport } from '~/services/report-exports/BTTExcelReport';
import { BTTPdfReport } from '~/services/report-exports/BTTPdfReport';
import BTTAlarmView from './btt-views/btt-alarm-view.vue';
import BTTBoltGraphView from './btt-views/btt-graph-view.vue';
import BTTHourCounterView from './btt-views/btt-hourcounter-view.vue';
import BTTInformationView from './btt-views/btt-information-view.vue';
import BTTPlacementView from './btt-views/btt-placement-view.vue';
import BTTStatusView from './btt-views/btt-status-view.vue';

const { hasIntermediateAccess, hasPremiumAccess } = useAuth();
const props = defineProps(['worksite', 'machine', 'decodedData', 'userData'])
interface TimeInformation {
  startTime: string | Date
  stopTime: string | Date
  totalTime: string
}

const formattedUser = computed(() => {
  if (!props.userData) return null;
  return {
    name: props.userData[3] + " " + props.userData[4],
  }
})

const formattedWorksite = computed(() => {
  if (!props.worksite) return null
  if (props.worksite.length === 5) {
      return {
          projectName: "Default Project",
          worksiteName: props.worksite[1],
          tunnelName: props.worksite[2],
          peg: props.worksite[3],
          screen: props.worksite[4]
      }
  };
  if (props.worksite.length < 8) return null;
    return {
        projectName: props.worksite[1],
        worksiteName: props.worksite[3],
        tunnelName: props.worksite[5],
        peg: props.worksite[6],
        screen: props.worksite[7]
    }
})

const formattedMachine = computed(() => {
  if (!props.machine || props.machine.length === 0) return null;

  return {
    id: props.machine[0],
    type: props.machine[3]
  }
})

const timeInformationProjectModelList = computed(() => props.decodedData.TimeInformationProjectData);
const formattedTimeInformation = computed((): TimeInformation | null => {
  const info = timeInformationProjectModelList.value;
  if (!info) {
    return null;
  }
    return {
      startTime: info.startTime,
      stopTime: info.stopTime,
      totalTime: info.totalTime
  };
});

const peginformation = computed(() => props.decodedData.StartAndEndPegNumberInfo);
const statusDataList = computed(() => props.decodedData.StatusData);
const boltGraphDataList = computed(() => props.decodedData.BoltGraphData);
const hoursDataList = computed(() => props.decodedData.HourCounterData);
const alarmsDataList = computed(() => props.decodedData.AlarmsData);

const allReportSections = ref([
  { id: 'Frontpage', label: 'Frontpage', selected: true },
  { id: 'Status', label: 'Status', selected: false },
  { id: 'BoltPlacement', label: 'Bolt Placement', selected: false },
  { id: 'BoltGraph', label: 'Bolt Graphs', selected: false },
  { id: 'HourCounter', label: 'Hour Counter', selected: false },
  { id: 'Alarms', label: 'Alarms', selected: false },
])
const reportSections = computed(() => {
  if (hasPremiumAccess.value) {
    return allReportSections.value;
  }
  if (hasIntermediateAccess.value) {
    return allReportSections.value.filter(s => !['BoltGraph'].includes(s.id));
  }
  return allReportSections.value.filter(s => ['Frontpage', 'Status', 'HourCounter', 'Alarms'].includes(s.id));
})
const showReportMenu = ref(false)
const generateReport = () => {
  const selectedSections = reportSections.value.filter(s => s.selected).map(s => s.id)
  const reportData = {
      selectedSections: selectedSections,
      worksite: formattedWorksite.value,
      machine: formattedMachine.value,
      timeInformation: formattedTimeInformation.value,
      peginformation: peginformation.value,
      statusDataList: statusDataList.value,
      boltGraphDataList: boltGraphDataList.value,
      hoursDataList: hoursDataList.value,
      alarmsDataList: alarmsDataList.value,
  };
  BTTPdfReport.exportToPdf(reportData);
  showReportMenu.value = false;
}

const generateExcelReport = () => {
  const selectedSections = reportSections.value.filter(s => s.selected).map(s => s.id)
  const reportData = {
      selectedSections: selectedSections,
      worksite: formattedWorksite.value,
      machine: formattedMachine.value,
      timeInformation: formattedTimeInformation.value,
      peginformation: peginformation.value,
      statusDataList: statusDataList.value,
      hoursDataList: hoursDataList.value,
      alarmsDataList: alarmsDataList.value,
  };
  BTTExcelReport.exportToExcel(reportData);
  showReportMenu.value = false;
}

const activeTab = ref('Information')
const tabList = ['Information', 'Status', 'Bolt Placement', 'Bolt Graphs', 'Hour Counter', 'Alarms']
</script>

<template>
  <v-container fluid class="report-container position-relative">
    <!-- Floating Action Button for Reports -->
    <div class="position-absolute top-0 right-0 mt-2 mr-2" style="z-index: 5">
      <v-menu v-model="showReportMenu" :close-on-content-click="false" location="bottom end">
        <template v-slot:activator="{ props }">
          <v-btn
            icon="mdi-file-document-outline"
            color="#002d5a"
            theme="dark"
            v-bind="props"
            size="large"
            class="elevation-4"
            title="Report Options"
          >
            <v-icon>mdi-file-document-outline</v-icon>
          </v-btn>
        </template>

        <v-card min-width="300">
          <v-card-title class="text-subtitle-1 font-weight-bold">Select Report Sections</v-card-title>
          <v-divider></v-divider>
          <v-card-text style="max-height: 300px; overflow-y: auto;">
            <v-checkbox
              v-for="section in reportSections"
              :key="section.id"
              v-model="section.selected"
              :label="section.label"
              density="compact"
              hide-details
              color="primary"
            ></v-checkbox>
          </v-card-text>
          <v-divider></v-divider>
          <v-card-actions class="justify-end">
            <v-btn variant="text" color="primary" @click="generateReport">PDF</v-btn>
            <v-btn variant="text" color="success" @click="generateExcelReport">Excel</v-btn>
          </v-card-actions>
        </v-card>
      </v-menu>
    </div>

    <!-- Dashboard Grid -->
    <v-row v-if="formattedWorksite || formattedMachine">
      <!-- Machine Card -->
      <v-col cols="12" md="4" v-if="formattedMachine">
        <v-card class="h-100" elevation="2">
          <v-card-title class="amv-yellow-bg text-white py-4">
            <div class="text-caption text-uppercase opacity-75">Machine Type</div>
            <div class="text-h6 font-weight-bold">{{ formattedMachine.type }}</div>
          </v-card-title>
          <v-card-text class="pt-4 d-flex flex-column justify-space-between">
            <div class="bg-grey-lighten-4 pa-4 rounded border mb-4 text-center">
              <div class="text-caption text-uppercase text-medium-emphasis font-weight-bold">Machine ID</div>
              <div class="text-h5 font-weight-bold text-primary font-monospace">{{ formattedMachine.id }}</div>
            </div>
            <div>
              <div class="text-caption text-uppercase text-medium-emphasis font-weight-bold">Operator</div>
              <div class="text-body-1 font-weight-medium">{{ formattedUser?.name }}</div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Project Card -->
      <v-col cols="12" md="4" v-if="formattedWorksite">
        <v-card class="h-100" elevation="2">
          <v-card-title class="amv-yellow-bg text-white py-4">
            <div class="text-caption text-uppercase opacity-75">Project</div>
            <div class="text-h6 font-weight-bold">{{ formattedWorksite.projectName }}</div>
          </v-card-title>
          <v-card-text class="pt-4">
            <v-row dense class="mb-2">
              <v-col cols="6">
                <div class="text-caption text-uppercase text-medium-emphasis font-weight-bold">Worksite</div>
                <div class="text-body-1 font-weight-medium">{{ formattedWorksite.worksiteName }}</div>
              </v-col>
              <v-col cols="6">
                <div class="text-caption text-uppercase text-medium-emphasis font-weight-bold">Tunnel</div>
                <div class="text-body-1 font-weight-medium">{{ formattedWorksite.tunnelName }}</div>
              </v-col>
            </v-row>
            <div class="bg-grey-lighten-4 pa-3 rounded border d-flex justify-space-around">
              <div class="text-center">
                <div class="text-caption text-uppercase text-medium-emphasis font-weight-bold">Peg Number</div>
                <div class="text-h5 font-weight-bold text-amv-yellow">{{ formattedWorksite.peg }}</div>
              </div>
              <div class="text-center">
                <div class="text-caption text-uppercase text-medium-emphasis font-weight-bold">Screen</div>
                <div class="text-h5 font-weight-bold text-amv-yellow">{{ formattedWorksite.screen }}</div>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Time Info Card -->
      <v-col cols="12" md="4" v-if="formattedTimeInformation">
        <v-card class="h-100" elevation="2">
          <v-card-title class="amv-yellow-bg text-white py-4">
            <div class="text-caption text-uppercase opacity-75">Production</div>
            <div class="text-h6 font-weight-bold">Time Info</div>
          </v-card-title>
          <v-card-text class="pt-4">
            <v-row dense class="mb-2">
              <v-col cols="6">
                <div class="text-caption text-uppercase text-medium-emphasis font-weight-bold">Start time</div>
                <div class="text-body-1 font-weight-medium">{{ formattedTimeInformation?.startTime }}</div>
              </v-col>
              <v-col cols="6">
                <div class="text-caption text-uppercase text-medium-emphasis font-weight-bold">Stop time</div>
                <div class="text-body-1 font-weight-medium">{{ formattedTimeInformation?.stopTime }}</div>
              </v-col>
            </v-row>
            <div class="bg-grey-lighten-4 pa-3 rounded border text-center">
              <div class="text-caption text-uppercase text-medium-emphasis font-weight-bold">Total Duration</div>
              <div class="text-h5 font-weight-bold text-amv-yellow">{{ formattedTimeInformation?.totalTime }}</div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Tabs Section -->
    <v-card class="mt-6" v-if="worksite.length > 0" elevation="2">
      <v-tabs v-model="activeTab" bg-color="grey-lighten-5" color="#002d5a" slider-color="#e5b000">
        <v-tab v-for="tab in tabList" :key="tab" :value="tab" class="text-capitalize font-weight-bold">
          {{ tab }}
        </v-tab>
      </v-tabs>

      <v-divider></v-divider>

      <v-card-text class="pa-4">
        <v-window v-model="activeTab">
          <v-window-item value="Information">
            <BTTInformationView :pegInformation="peginformation" />
          </v-window-item>
          <v-window-item value="Status">
            <BTTStatusView :statusData="statusDataList" />
          </v-window-item>
          <v-window-item value="Bolt Placement">
            <BTTPlacementView :boltGraphData="boltGraphDataList" />
          </v-window-item>
          <v-window-item value="Bolt Graphs">
            <BTTBoltGraphView :boltGraphData="boltGraphDataList" />
          </v-window-item>
          <v-window-item value="Hour Counter">
            <BTTHourCounterView :hoursData="hoursDataList" />
          </v-window-item>
          <v-window-item value="Alarms">
            <BTTAlarmView :alarmsData="alarmsDataList"/>
          </v-window-item>
        </v-window>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<style scoped>
.amv-yellow-bg {
  background-color: #e5b000 !important;
}
.text-amv-yellow {
  color: #e5b000 !important;
}
.font-monospace {
  font-family: 'Courier New', Courier, monospace !important;
}
</style>